import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, format, isSameDay, startOfMonth, subMonths } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getPublishedRoster, getRosterPublicationStatus, RosterPublicationStatus } from '../../services/rosterService';
import RosterAvailabilityNotice from './RosterAvailabilityNotice';

const shiftTheme: Record<string, string> = {
    CLEARER: 'bg-blue-50 text-blue-700 border-blue-200',
    LHR: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    LGW: 'bg-sky-50 text-sky-700 border-sky-200',
    CONTROL: 'bg-purple-50 text-purple-700 border-purple-200',
    OFFICE: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    WAREHOUSE: 'bg-amber-50 text-amber-700 border-amber-200',
};

const getShiftDisplayCode = (shift: any) => {
    if (!shift) return '';

    const type = String(shift.type || '').toUpperCase();
    const location = String(shift.location || '').toUpperCase();

    if (type === 'CLEARER') {
        if (location.includes('LHR') || location.includes('HEATHROW')) return 'LHR';
        if (location.includes('LGW') || location.includes('GATWICK')) return 'LGW';
        return 'LGW';
    }

    return type || 'SHIFT';
};

const shiftLabel = (shift: any) => {
    if (!shift) return '';
    return 'Rostered shift';
};

const getShiftsForDate = (member: any, dateKey: string) => {
    const shift = member.shifts?.[dateKey];
    if (!shift) return [];
    return [shift, ...(shift._additionalShifts || [])];
};

const getInitialDate = () => new Date();

const TeamRoster = () => {
    const [anchorDate, setAnchorDate] = useState(getInitialDate);
    const [selectedDate, setSelectedDate] = useState(getInitialDate);
    const [rosterData, setRosterData] = useState<any>(null);
    const [publicationStatus, setPublicationStatus] = useState<RosterPublicationStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showMonthMap, setShowMonthMap] = useState(false);
    const [showFullscreenGrid, setShowFullscreenGrid] = useState(false);
    const dateStripRef = useRef<HTMLDivElement | null>(null);
    const selectedDateButtonRef = useRef<HTMLButtonElement | null>(null);

    const visibleDays = useMemo(
        () => eachDayOfInterval({ start: startOfMonth(anchorDate), end: endOfMonth(anchorDate) }),
        [anchorDate]
    );

    const loadRoster = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [data, status] = await Promise.all([
                getPublishedRoster(anchorDate.getFullYear(), anchorDate.getMonth() + 1),
                getRosterPublicationStatus(anchorDate.getFullYear(), anchorDate.getMonth() + 1),
            ]);
            setRosterData(data);
            setPublicationStatus(status);
        } catch (err: any) {
            setError(err.response?.data?.error?.message || err.message || 'Failed to load team roster');
        } finally {
            setLoading(false);
        }
    }, [anchorDate]);

    useEffect(() => { loadRoster(); }, [loadRoster]);

    const versionStatus = rosterData?.version?.status;
    const staff = rosterData?.staff || [];
    const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
    const workingStaff = staff
        .map((member: any) => ({ member, shifts: getShiftsForDate(member, selectedDateKey) }))
        .filter((item: any) => item.shifts.length > 0);
    const offStaff = staff.filter((member: any) => getShiftsForDate(member, selectedDateKey).length === 0);
    const today = new Date();

    const centerSelectedDate = useCallback((date = selectedDate) => {
        const handle = window.setTimeout(() => {
            const container = dateStripRef.current || document.querySelector<HTMLElement>('[data-date-strip="true"]');
            const selectedButton = selectedDateButtonRef.current || document.querySelector<HTMLElement>(`[data-roster-date="${format(date, 'yyyy-MM-dd')}"]`);
            if (!container || !selectedButton) return;

            container.scrollLeft = selectedButton.offsetLeft - (container.clientWidth / 2) + (selectedButton.clientWidth / 2);
        }, 50);
        return () => window.clearTimeout(handle);
    }, [selectedDate]);

    useEffect(() => {
        return centerSelectedDate();
    }, [selectedDate, versionStatus]);

    const moveMonth = (direction: -1 | 1) => {
        const nextMonth = direction === -1 ? subMonths(anchorDate, 1) : addMonths(anchorDate, 1);
        setAnchorDate(nextMonth);
        setSelectedDate(startOfMonth(nextMonth));
    };

    const goToday = () => {
        setAnchorDate(today);
        setSelectedDate(today);
        centerSelectedDate(today);
    };

    const selectDay = (day: Date, closeMap = false) => {
        setSelectedDate(day);
        if (closeMap) setShowMonthMap(false);
    };

    const workingCountForDay = (day: Date) => {
        const dateKey = format(day, 'yyyy-MM-dd');
        return staff.reduce((count: number, member: any) => count + (getShiftsForDate(member, dateKey).length > 0 ? 1 : 0), 0);
    };

    const ShiftBadge = ({ shift, compact = false }: { shift: any; compact?: boolean }) => {
        const displayCode = getShiftDisplayCode(shift);
        const theme = shiftTheme[displayCode] || shiftTheme[shift?.type] || 'bg-slate-50 text-slate-700 border-slate-200';
        return (
            <div className={`rounded-md border ${compact ? 'px-2 py-1.5 text-center' : 'px-3 py-2'} ${theme}`}>
                <p className={compact ? 'text-[11px] font-bold' : 'text-sm font-bold'}>{displayCode}</p>
                <p className={compact ? 'text-[11px] font-semibold' : 'text-sm font-semibold'}>{shiftLabel(shift)}</p>
                <div className="mt-1 flex flex-col gap-1">
                    {shift.needsCover && <span className="rounded bg-amber-100 px-1 text-[10px] font-bold text-amber-700">Needs cover</span>}
                    {shift.assignmentChanged && <span className="rounded bg-white/70 px-1 text-[10px] font-bold text-slate-700">{shift.assignmentChangeType}</span>}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Team Roster</h1>
                        {versionStatus && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                                Published
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{format(anchorDate, 'MMMM yyyy')}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <button
                        onClick={() => setShowFullscreenGrid(true)}
                        className="px-4 py-2 text-sm font-semibold rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                        Fullscreen
                    </button>
                    <div className="grid grid-cols-[1fr_auto_1fr] md:flex bg-white border border-slate-200 rounded-lg overflow-hidden">
                        <button onClick={() => moveMonth(-1)} className="flex items-center justify-center gap-1 px-2 py-2 text-slate-500 hover:bg-slate-100" aria-label="Previous month">
                            <ChevronLeft className="w-4 h-4" />
                            <span className="md:hidden text-xs font-semibold">Prev month</span>
                        </button>
                        <button onClick={goToday} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 border-x border-slate-200">
                            Today
                        </button>
                        <button onClick={() => moveMonth(1)} className="flex items-center justify-center gap-1 px-2 py-2 text-slate-500 hover:bg-slate-100" aria-label="Next month">
                            <span className="md:hidden text-xs font-semibold">Next month</span>
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {loading && <div className="bg-white border border-slate-200 rounded-lg p-8 text-slate-500">Loading team roster...</div>}
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>}

            {!loading && !error && publicationStatus && publicationStatus !== 'PUBLISHED' && (
                <RosterAvailabilityNotice status={publicationStatus} />
            )}

            {!loading && !error && publicationStatus === 'PUBLISHED' && versionStatus && (
                <>
                <div className="md:hidden space-y-4">
                    <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <div ref={dateStripRef} data-date-strip="true" className="flex gap-2 overflow-x-auto pb-1 snap-x">
                            {visibleDays.map(day => {
                                const active = isSameDay(day, selectedDate);
                                const isToday = isSameDay(day, today);
                                const workingCount = workingCountForDay(day);
                                return (
                                    <button
                                        key={day.toISOString()}
                                        data-roster-date={format(day, 'yyyy-MM-dd')}
                                        ref={active ? selectedDateButtonRef : undefined}
                                        onClick={() => selectDay(day)}
                                        className={`relative snap-start shrink-0 w-14 rounded-lg border px-2 py-2 text-center ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : isToday ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500'}`}
                                    >
                                        <span className="block text-[10px] font-bold uppercase">{format(day, 'EEE')}</span>
                                        <span className="block text-lg font-bold leading-tight">{format(day, 'd')}</span>
                                        {isToday && <span className="mt-1 block text-[9px] font-bold uppercase">Today</span>}
                                        {workingCount > 0 && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        onClick={() => setShowMonthMap(true)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between text-left"
                    >
                        <span>
                            <span className="block text-xs font-bold uppercase tracking-wide text-slate-500">Month map</span>
                            <span className="block text-sm font-semibold text-slate-800">Open full month overview</span>
                        </span>
                        <CalendarDays className="w-5 h-5 text-blue-600" />
                    </button>

                    <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Selected day</p>
                                <h2 className="text-xl font-bold text-slate-900">{format(selectedDate, 'EEEE, d MMM')}</h2>
                            </div>
                            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                                {workingStaff.length} working
                            </span>
                        </div>
                    </div>

                    {workingStaff.length === 0 ? (
                        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-slate-500">
                            No staff are rostered on this day.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {workingStaff.map(({ member, shifts }: any) => (
                                <div key={member.id} className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-bold text-slate-900 truncate">{member.name}</p>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{member.role}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        {shifts.map((shift: any) => <ShiftBadge key={shift.id} shift={shift} />)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <details className="bg-white border border-slate-200 rounded-lg">
                        <summary className="cursor-pointer list-none px-4 py-3 font-semibold text-slate-700">
                            Off duty staff ({offStaff.length})
                        </summary>
                        <div className="border-t border-slate-100 divide-y divide-slate-100">
                            {offStaff.map((member: any) => (
                                <div key={member.id} className="px-4 py-3">
                                    <p className="font-semibold text-slate-800">{member.name}</p>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{member.role}</p>
                                </div>
                            ))}
                        </div>
                    </details>

                    {showMonthMap && (
                        <div className="fixed inset-0 z-50 bg-white md:hidden overflow-y-auto pb-6">
                            <div className="p-4 space-y-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Team roster</p>
                                        <h2 className="text-2xl font-bold text-slate-900">{format(anchorDate, 'MMMM yyyy')}</h2>
                                    </div>
                                    <button
                                        onClick={() => setShowMonthMap(false)}
                                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
                                        aria-label="Close month map"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-7 gap-1 text-center">
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                        <div key={day} className="py-2 text-[11px] font-bold uppercase text-slate-400">{day}</div>
                                    ))}
                                    {Array.from({ length: (Number(format(startOfMonth(anchorDate), 'i')) - 1) }).map((_, index) => (
                                        <div key={`blank-${index}`} />
                                    ))}
                                    {visibleDays.map(day => {
                                        const active = isSameDay(day, selectedDate);
                                        const isToday = isSameDay(day, today);
                                        const workingCount = workingCountForDay(day);
                                        return (
                                            <button
                                                key={day.toISOString()}
                                                onClick={() => selectDay(day, true)}
                                                className={`min-h-[74px] rounded-lg border p-1.5 text-left ${active ? 'border-blue-600 bg-blue-50' : isToday ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                                            >
                                                <span className={`block text-sm font-bold ${active ? 'text-blue-700' : isToday ? 'text-emerald-700' : 'text-slate-800'}`}>{format(day, 'd')}</span>
                                                {isToday && <span className="mt-0.5 inline-block rounded bg-emerald-100 px-1 text-[9px] font-bold uppercase text-emerald-700">Today</span>}
                                                <span className="mt-1 block text-[10px] font-semibold text-slate-500">{workingCount} working</span>
                                                {workingCount > 0 && (
                                                    <span className="mt-1 flex gap-0.5">
                                                        {Array.from({ length: Math.min(workingCount, 4) }).map((_, index) => (
                                                            <span key={index} className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                        ))}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="hidden md:block bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <div className="overflow-auto max-h-[calc(100vh-220px)]">
                        <table className="min-w-[1180px] w-full border-collapse text-sm">
                            <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                                <tr>
                                    <th className="sticky left-0 z-20 bg-slate-50 border-b border-r border-slate-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 min-w-[180px]">
                                        Staff
                                    </th>
                                    {visibleDays.map(day => (
                                        <th key={day.toISOString()} className="border-b border-r border-slate-200 px-2 py-2 text-center min-w-[86px]">
                                            <span className="block text-[11px] font-bold uppercase text-slate-400">{format(day, 'EEE')}</span>
                                            <span className="block text-base font-bold text-slate-700">{format(day, 'd')}</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {staff.map((member: any) => (
                                    <tr key={member.id} className="border-b border-slate-100">
                                        <th className="sticky left-0 z-10 bg-white border-r border-slate-200 px-4 py-3 text-left align-top min-w-[180px]">
                                            <p className="font-semibold text-slate-800">{member.name}</p>
                                            <p className="text-xs text-slate-400">{member.role}</p>
                                        </th>
                                        {visibleDays.map(day => {
                                            const dateKey = format(day, 'yyyy-MM-dd');
                                            const shifts = getShiftsForDate(member, dateKey);
                                            return (
                                                <td key={`${member.id}-${dateKey}`} className="border-r border-slate-100 px-2 py-2 align-top h-16">
                                                    {shifts.length > 0 ? (
                                                        <div className="space-y-1.5">
                                                            {shifts.map((shift: any) => <ShiftBadge key={shift.id} shift={shift} compact />)}
                                                        </div>
                                                    ) : (
                                                        <span className="block text-center text-xs text-slate-300">-</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {showFullscreenGrid && (
                    <div className="fixed inset-0 z-[70] bg-white flex flex-col">
                        <div className="h-14 shrink-0 border-b border-slate-200 px-3 sm:px-5 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-900 truncate">Fullscreen team roster</p>
                                <p className="text-xs text-slate-500 truncate">{format(anchorDate, 'MMMM yyyy')} · read-only quick view</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => moveMonth(-1)} className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">
                                    Prev
                                </button>
                                <button onClick={goToday} className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">
                                    Today
                                </button>
                                <button onClick={() => moveMonth(1)} className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">
                                    Next
                                </button>
                                <button
                                    onClick={() => setShowFullscreenGrid(false)}
                                    className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                                    aria-label="Close fullscreen roster"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 min-h-0 overflow-auto">
                            <table className="min-w-[1180px] w-full border-separate border-spacing-0 text-sm">
                                <thead>
                                    <tr>
                                        <th className="sticky top-0 left-0 z-30 bg-slate-50 border-b border-r border-slate-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 min-w-[180px]">
                                            Staff
                                        </th>
                                        {visibleDays.map(day => (
                                            <th key={day.toISOString()} className="sticky top-0 z-20 bg-slate-50 border-b border-r border-slate-200 px-2 py-2 text-center min-w-[86px]">
                                                <span className="block text-[11px] font-bold uppercase text-slate-400">{format(day, 'EEE')}</span>
                                                <span className="block text-base font-bold text-slate-700">{format(day, 'd')}</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {staff.map((member: any) => (
                                        <tr key={member.id}>
                                            <th className="sticky left-0 z-10 bg-white border-b border-r border-slate-200 px-4 py-3 text-left align-top min-w-[180px]">
                                                <p className="font-semibold text-slate-800">{member.name}</p>
                                                <p className="text-xs text-slate-400">{member.role}</p>
                                            </th>
                                            {visibleDays.map(day => {
                                                const dateKey = format(day, 'yyyy-MM-dd');
                                                const shifts = getShiftsForDate(member, dateKey);
                                                return (
                                                    <td key={`${member.id}-${dateKey}`} className="border-b border-r border-slate-100 px-2 py-2 align-top h-16">
                                                        {shifts.length > 0 ? (
                                                            <div className="space-y-1.5">
                                                                {shifts.map((shift: any) => <ShiftBadge key={shift.id} shift={shift} compact />)}
                                                            </div>
                                                        ) : (
                                                            <span className="block text-center text-xs text-slate-300">-</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                </>
            )}
        </div>
    );
};

export default TeamRoster;
